/**
 * P201 follow-on -- map a rail metric to the set of constellation NODE ids
 * it concerns, so clicking a metric highlights those nodes on the canvas
 * (reusing the search-highlight visual). Pure + testable.
 *
 * The metrics are cascade aggregates; this resolves each to the real
 * impacted entities behind it (matched against the graph's node ids, so
 * only nodes actually on the canvas light up):
 *   ibsImpacted / revenueDaily -> impacted IBS nodes (revenue flows via IBSes)
 *   criticalFindings           -> impacted nodes with a critical headline
 *   mvbIntersection            -> impacted IBS nodes flagged MVB
 *   channelsDegraded           -> impacted channel nodes
 */
import type { CascadeSummary, TwinNode } from "shared/api/types";
import type { MetricKey } from "twin/constellation/MetricsRail";

export function nodesForMetric(
  key: MetricKey,
  cascade: CascadeSummary | null | undefined,
  nodes: TwinNode[],
): Set<string> {
  const out = new Set<string>();
  if (!cascade) return out;
  const nodeIds = new Set(nodes.map((n) => n.id));
  const mvbIds = new Set(nodes.filter((n) => n.mvb_flag).map((n) => n.id));

  for (const imp of cascade.impacted) {
    if (!nodeIds.has(imp.entity_id)) continue;
    const isIbs = imp.entity_kind === "ibs_service";
    const isChannel = imp.entity_kind === "channel";
    const isCritical = imp.headline_finding?.severity === "critical";
    let match = false;
    switch (key) {
      case "ibsImpacted":
      case "revenueDaily":
        match = isIbs;
        break;
      case "criticalFindings":
        match = isCritical;
        break;
      case "mvbIntersection":
        match = isIbs && mvbIds.has(imp.entity_id);
        break;
      case "channelsDegraded":
        match = isChannel;
        break;
    }
    if (match) out.add(imp.entity_id);
  }
  return out;
}

/**
 * Round 9 F2 -- where a metric's rows are LISTED.
 *
 * Eighth round for "clicking 10 IBSES IMPACTED highlights nodes, opens
 * nothing and routes nowhere". A prior round made it highlight, which
 * was the right answer to the 404 it replaced: these are cascade
 * AGGREGATES with no chain entity of their own, so there was nowhere
 * to send anybody. Then the board's identical figure gained a
 * destination -- the cascade, filtered to what the tile counted -- and
 * the canvas kept pointing at nothing.
 *
 * Only where the filter renders EXACTLY what the figure counted.
 * That is the whole discipline of the board's own fix: an unfiltered
 * cascade under a tile saying ten is two honest answers a reader has
 * to reconcile as a contradiction. `revenueDaily` and
 * `mvbIntersection` return null deliberately -- the cascade has no
 * revenue filter and no MVB filter, so any link from them would show
 * more rows than the number that was pressed.
 */
export function listRouteForMetric(key: MetricKey): string | null {
  switch (key) {
    case "ibsImpacted":
      return "/twin/cascade?kind=ibs_service";
    case "criticalFindings":
      return "/twin/cascade?severity=critical";
    case "channelsDegraded":
      return "/twin/cascade?kind=channel";
    case "revenueDaily":
    case "mvbIntersection":
      return null;
  }
}
